import {IMessage} from '@stomp/stompjs';
import {useEffect, useRef} from 'react';
import {useStompClient} from './StopmClientContext';
import {AckEvent, LobbyEvent} from "./events";

export function useLobbySocket({
                                   lobbyId,
                                   onLobbyEvent,
                                   onAck,
                               }: {
    lobbyId: number;
    onLobbyEvent: (e: LobbyEvent) => void;
    onAck?: (e: AckEvent) => void;
}) {
    const {client, connected, publish} = useStompClient();

    const lobbyIdRef = useRef(lobbyId);
    lobbyIdRef.current = lobbyId;

    const handlersRef = useRef({onLobbyEvent, onAck});
    handlersRef.current = {onLobbyEvent, onAck};

    useEffect(() => {
        if (!client || !connected) {
            return;
        }

        const lid = lobbyIdRef.current;

        const subPublic = client.subscribe(`/topic/lobby/${lid}`,
            (m: IMessage) => handlersRef.current.onLobbyEvent(JSON.parse(m.body)),
        );

        const subPrivate = client.subscribe(`/user/queue/lobby/${lid}`,
            (m: IMessage) => handlersRef.current.onLobbyEvent(JSON.parse(m.body)),
        );

        const subAck = client.subscribe(`/user/queue/ack/lobby/${lid}`,
            (m: IMessage) => handlersRef.current.onAck?.(JSON.parse(m.body)),
        );

        publish(`/app/lobby.${lid}.sync`, {requestId: crypto.randomUUID()});

        return () => {
            subPublic.unsubscribe();
            subPrivate.unsubscribe();
            subAck.unsubscribe();
        };
    }, [client, connected, lobbyId, publish]);

    const ready = (ready: boolean) =>
        publish(`/app/lobby.${lobbyIdRef.current}.ready`, {
            requestId: crypto.randomUUID(),
            ready,
        });

    const start = () =>
        publish(`/app/lobby.${lobbyIdRef.current}.start`, {
            requestId: crypto.randomUUID(),
        });

    const sync = () =>
        publish(`/app/lobby.${lobbyIdRef.current}.sync`, {
            requestId: crypto.randomUUID(),
        });

    const leave = () =>
        publish(`/app/lobby.${lobbyIdRef.current}.leave`, {
            requestId: crypto.randomUUID(),
        });

    return {ready, start, sync, leave};
}
